import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import { FC, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import DealSetupFooter from './components/DealSetupFooter';
import {
  handlePercentageChange,
  createFormattedPercentValue,
  stripPercentSymbol,
} from '@utils/percentageInputsUtils';
import { menuItemStyles } from '@utils/styles';
import {
  ReferrerDetails,
  ReferrerType,
  useGetReferrersPaginatedInfiniteQuery,
  useLazyGetReferrersByGameIdQuery,
  useMapReferrersToDealMutation,
} from '@api/referrers';
import { constructFetchGamesQueryString } from '@utils/common';
import { InfiniteSearchSelect } from '@components';

type ReferrerDetailsState = Omit<ReferrerDetails, 'status' | 'type' | 'referrerId'> & {
  id: string;
  type: '' | ReferrerType;
};

const defaultReferrerDetails: ReferrerDetailsState = {
  id: '',
  email: '',
  commissionPercentage: '',
  type: '',
  numberOfCohorts: null,
};

const referrerTypeOptions = [
  { label: 'Perpetual', value: ReferrerType.PERPETUAL },
  { label: 'Cohort based', value: ReferrerType.COHORT_BASED },
];

const getUniqueId = () => {
  return crypto.randomUUID();
};
interface ReferrerDetailsTabProps {
  handleCancelClick: () => void;
  gameId: string;
  dealUpdatedDetailsComponent: JSX.Element;
}

const ReferrerDetailsTab: FC<ReferrerDetailsTabProps> = ({
  handleCancelClick,
  dealUpdatedDetailsComponent,
  gameId,
}) => {
  const [newReferrerDetails, setNewReferrerDetails] = useState<ReferrerDetailsState[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [getReferrersByGameId, { data: referrersByGameId, isLoading: isReferrersLoading }] =
    useLazyGetReferrersByGameIdQuery();

  const queryString = useMemo(() => {
    return constructFetchGamesQueryString({
      pageSize: 20,
      search: searchTerm,
    });
  }, [searchTerm]);
  const {
    data: referrers,
    hasNextPage,
    isFetching,
    fetchNextPage,
    isLoading: isLoadingGames,
    isError,
  } = useGetReferrersPaginatedInfiniteQuery(queryString);
  const [saveReferrers, { isLoading: isSaving }] = useMapReferrersToDealMutation();

  const currentReferrersWithIds = useMemo(() => {
    return (
      referrersByGameId?.data.map((referrer) => ({
        ...referrer,
        type: referrer.type || '',
      })) || []
    );
  }, [referrersByGameId?.data]);

  useEffect(() => {
    getReferrersByGameId(gameId);
  }, [gameId]);

  useEffect(() => {
    if (
      referrersByGameId?.data.length === 0 &&
      newReferrerDetails.length === 0 &&
      !isReferrersLoading &&
      !isSaving
    ) {
      setNewReferrerDetails([{ ...defaultReferrerDetails, id: getUniqueId() }]);
    }
  }, [referrersByGameId?.data, newReferrerDetails, isReferrersLoading, isSaving]);

  const isSaveEnabled = useMemo(() => {
    if (newReferrerDetails.length === 0) return false;

    const keys = Object.keys(defaultReferrerDetails).filter((key) => key !== 'numberOfCohorts');
    const hasAnyDefaultValue = (referrer: ReferrerDetailsState) =>
      keys.some(
        (key) =>
          referrer[key as keyof ReferrerDetailsState] ===
          defaultReferrerDetails[key as keyof ReferrerDetailsState],
      );
    return newReferrerDetails.every((referrer) => !hasAnyDefaultValue(referrer));
  }, [newReferrerDetails]);

  const emailOptions = useMemo(() => {
    const currentReferrerEmails = currentReferrersWithIds.map((referrer) => referrer.email);
    return (
      referrers?.pages
        .flatMap((page) => page.data.items)
        .filter((referrer: { email: string }) => !currentReferrerEmails.includes(referrer.email))
        .map((referrer: { email: string }) => ({
          label: referrer.email,
          value: referrer.email,
        })) || []
    );
  }, [referrers, currentReferrersWithIds]);

  const handleEmailSelect = (id: string, email: string) => {
    const referrer = referrers?.pages
      .flatMap((page) => page.data.items)
      .find((referrer: { email: string }) => referrer.email === email);
    if (referrer) {
      const { commissionPercentage, type, numberOfCohorts } = referrer;
      updateReferrer(id, {
        email,
        type,
        numberOfCohorts: type === ReferrerType.PERPETUAL ? null : numberOfCohorts,
        commissionPercentage: createFormattedPercentValue(Number(commissionPercentage) * 100),
      });
    }
  };

  const renderSelectField = (
    labelId: string,
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { label: string; value: string }[],
    isDisabled: boolean = false,
  ) => {
    return (
      <>
        <InputLabel id={labelId}>{label}</InputLabel>
        <Select
          labelId={labelId}
          disabled={options.length === 0 || isDisabled}
          value={value}
          label={label}
          onChange={(e) => {
            onChange(e.target.value.toString());
          }}
          sx={{
            height: '56px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6C6C6C',
            },
            '& .MuiPaper-root': {
              maxHeight: '300px',
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={menuItemStyles}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </>
    );
  };

  const addReferrer = () => {
    setNewReferrerDetails([
      ...newReferrerDetails,
      { ...defaultReferrerDetails, id: getUniqueId() },
    ]);
  };

  const updateReferrer = (id: string, updatedFields: Partial<ReferrerDetails>) => {
    setNewReferrerDetails((prev) =>
      prev.map((referral) => (referral.id === id ? { ...referral, ...updatedFields } : referral)),
    );
  };

  const getViewReferrerComponent = (referrer: ReferrerDetailsState) => {
    return (
      <Box
        key={referrer.id}
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '1rem',
          alignItems: 'center',
          width: '100%',
          paddingRight: '2.5rem',
        }}
      >
        <TextField
          label="Registered email ID"
          sx={{
            width: isMobile ? '100%' : '35%',
            pointerEvents: 'none',
          }}
          value={referrer.email}
        />
        <TextField
          label="% Commission"
          sx={{
            width: isMobile ? '100%' : '20%',
            pointerEvents: 'none',
          }}
          value={createFormattedPercentValue(Number(referrer.commissionPercentage) * 100)}
        />
        <TextField
          label="Type"
          sx={{
            width: isMobile ? '100%' : '25%',
            pointerEvents: 'none',
          }}
          value={referrerTypeOptions.find((option) => option.value === referrer.type)?.label}
        />
        <TextField
          label="No. of Cohorts eligible"
          sx={{
            width: isMobile ? '100%' : '20%',
            pointerEvents: 'none',
          }}
          value={referrer.numberOfCohorts}
        />
      </Box>
    );
  };

  const getAddReferrerComponent = (referrer: ReferrerDetailsState) => {
    return (
      <Box
        key={referrer.id}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1rem',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '1rem',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <InfiniteSearchSelect
            placeholder="Registered email ID"
            options={emailOptions}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            isLoading={isLoadingGames}
            isError={isError}
            errorText="Error loading emails"
            noOptionsText="No emails found"
            selectStyles={{ width: isMobile ? '100%' : '35%' }}
            onSearchChange={(value) => {
              setSearchTerm(value);
            }}
            selectedValue={referrer.email}
            onSelect={(email) => {
              handleEmailSelect(referrer.id, email.value);
            }}
          />
          <TextField
            label="% Commission"
            placeholder="enter %value"
            sx={{ width: isMobile ? '100%' : '20%' }}
            value={referrer.commissionPercentage}
            disabled={!Boolean(referrer.email)}
            onChange={(e) => {
              handlePercentageChange(
                e,
                (result) => {
                  updateReferrer(referrer.id, {
                    commissionPercentage: result,
                  });
                },
                25,
              );
            }}
          />
          <FormControl sx={{ width: isMobile ? '100%' : '25%' }}>
            {renderSelectField(
              `type-${referrer.id}`,
              'Type',
              referrer.type,
              (value) =>
                updateReferrer(referrer.id, {
                  type: value as ReferrerType,
                  ...(value === ReferrerType.PERPETUAL
                    ? { numberOfCohorts: null }
                    : { numberOfCohorts: 0 }),
                }),
              referrerTypeOptions,
              !Boolean(referrer.email),
            )}
          </FormControl>
          <TextField
            label="No. of Cohorts eligible"
            sx={{ width: isMobile ? '100%' : '20%', padding: '0' }}
            type="number"
            value={referrer.numberOfCohorts !== null ? referrer.numberOfCohorts : ''}
            disabled={referrer.type === ReferrerType.PERPETUAL || !Boolean(referrer.email)}
            onChange={(e) => {
              const value = e.target.value;
              updateReferrer(referrer.id, {
                numberOfCohorts: value === '' ? null : Number(value),
              });
            }}
          />
        </Box>
        <DeleteOutlinedIcon
          sx={{
            cursor: 'pointer',
            fill: '#2F736E',
          }}
          onClick={() => {
            if (
              newReferrerDetails.length > 1 ||
              (referrersByGameId?.data && referrersByGameId.data.length > 0)
            )
              setNewReferrerDetails((prev) =>
                prev.filter((prevReferrer) => prevReferrer.id !== referrer.id),
              );
          }}
        />
      </Box>
    );
  };

  const handleSave = () => {
    const validReferrers = newReferrerDetails
      .filter((ref) => ref.type !== '')
      .map((ref) => ({
        referrerId:
          referrers?.pages
            .flatMap((page) => page.data.items)
            .find((referrer: { email: string }) => referrer.email === ref.email)?.referrerId || '',
        email: ref.email,
        commissionPercentage: stripPercentSymbol(ref.commissionPercentage) / 100,
        type: ref.type as ReferrerType,
        numberOfCohorts: ref.numberOfCohorts,
      }));
    saveReferrers({
      gameId,
      referrerDealMappingDetails: validReferrers,
    })
      .unwrap()
      .then(async () => {
        await getReferrersByGameId(gameId).unwrap();
        setNewReferrerDetails([]);
        setSearchTerm('');
      });
  };

  if (isReferrersLoading || isSaving) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress
          style={{
            width: '4rem',
            height: '4rem',
            color: theme.palette.primary.main,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: '1',
          pr: '0.5rem',
          overflowY: 'auto',
          pb: '1rem',
        }}
      >
        <Typography
          fontSize={'1rem'}
          fontWeight={700}
          sx={{ color: '#162936', marginBottom: '1.25rem' }}
        >
          Referrer Details
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '4rem' : '1rem',
            width: '100%',
          }}
        >
          {currentReferrersWithIds.map((referrer) =>
            getViewReferrerComponent({
              ...referrer,
              id: referrer.referrerId,
            }),
          )}
          {newReferrerDetails.map((referrer) => getAddReferrerComponent(referrer))}
        </Box>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          variant="outlined"
          onClick={addReferrer}
          sx={{
            marginTop: '1rem',
            borderRadius: '2rem',
            padding: '0.625rem 1.5rem',
            alignItems: 'center',
            textTransform: 'none',
            color: '#2F736E',
          }}
        >
          Add More Referrer
        </Button>
      </Box>
      <DealSetupFooter
        dealUpdatedDetailsComponent={dealUpdatedDetailsComponent}
        onCancel={handleCancelClick}
        isSubmitEnabled={isSaveEnabled && !isReferrersLoading}
        onSave={handleSave}
      />
    </Box>
  );
};

export default ReferrerDetailsTab;
